import { onUnmounted, ref } from 'vue'
import type { IntakeChoice } from '@/composables/useJobIntake'

// ── THE INTAKE DOOR ─────────────────────────────────────────────────────────
// ONE verb in the app header — « Nouveau job » — opening ONE form (design 165):
// the form asks the deal's state itself, so the door carries no intent any more
// (the three-door panel and its « Reprendre » recents died with the question).
// The call can still be captured from wherever the dispatcher happens to be
// standing, which is why the door lives in the shell and not on a page.
//
// This module owns what a global door needs and a page cannot hold:
//   · whether the form is open, and with what anchor / type prefill
//   · onIntakeCreated — how a page learns something was born
//
// Module-level state, deliberately: the trigger lives in the layout, the
// dialog lives in the layout, and the pages that must react are somewhere else
// entirely. A per-component composable could not join those three.

// What a page is told when the door produced something: the container that
// was born, and the card it was born under.
export type IntakeCreated = { intake: IntakeChoice; jobId: string }

// The ADDRESS ANCHOR — the spawn doors (« Nouveau job ici » on a job page, a
// location page, a person's or a company's page) open the same form with the
// customer already answered and LOCKED: the address IS the family, a spawn
// means « a new job here ». The anchoring JOB rides beside it as the new job's
// parent (the chain: « Issu de » on the child, « Créés depuis ce job » on the
// parent; born assessed on the lead lane when the parent's look-see is done).
// A location page's door has no job, so it stamps none.
export type CaptureAnchor = { serviceLocationId: string; originJobId?: string }

const captureOpen = ref(false)
const captureTemplateId = ref<string | undefined>(undefined)
const captureAnchor = ref<CaptureAnchor | undefined>(undefined)

// ── The subscribers ─────────────────────────────────────────────────────────
// A page reconciles its own feeds when the door produces something. The org
// cable would say the same thing ~2 s later; a dispatcher must not wait on a
// broadcast to see their own act.
const listeners = new Set<(created: IntakeCreated) => void>()

export function useIntakeDoors() {
  // The door WIPES on open: the anchor and any draft belong to the conversation
  // that is starting, never to the one that was abandoned.
  const launch = (anchor?: CaptureAnchor, templateId?: string) => {
    captureTemplateId.value = templateId
    captureAnchor.value = anchor
    captureOpen.value = true
  }

  const closeCapture = () => {
    captureOpen.value = false
    captureTemplateId.value = undefined
    captureAnchor.value = undefined
  }

  const created = (payload: IntakeCreated) => {
    for (const listener of listeners) listener(payload)
  }

  // Subscribe for as long as the calling component lives. Called from a page's
  // setup, so the unmount hook is the right owner; a listener that outlived its
  // page would refetch into a dead component.
  const onIntakeCreated = (callback: (payload: IntakeCreated) => void) => {
    listeners.add(callback)
    onUnmounted(() => { listeners.delete(callback) })
  }

  return {
    captureOpen, captureTemplateId, captureAnchor,
    launch, closeCapture,
    created, onIntakeCreated,
  }
}
